import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import pluralize from "pluralize";
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { RiCheckboxCircleLine, RiErrorWarningLine } from "@remixicon/react";

import { ShippingDialogProps } from "../../../types/Shipping";
import BaseShippingDialog from "./BaseShippingDialog";
import {
  ValidationError_T,
  hasInvalidAppleCustomerSelection,
  hasExportDateOutsideShippingWeek,
  getMissingDestinations,
  hasValidDeliveryStatusForGroup,
} from "../../../utils/shippingManagementValidators";
import { useDataPlorApi } from "../../../hooks/useDataplorApi";

type DeliveryValidationError_T = ValidationError_T & {
  fulfillment_group_shipment_id: number | null;
};

const DeliverShipmentDialog: React.FC<ShippingDialogProps> = (props) => {
  const theme = useTheme();
  const { postDataplorApi } = useDataPlorApi();

  const { selectedData } = props;
  const {
    fullySelectedGroups,
    partiallySelectedGroups,
    totalSelectedShipments,
  } = selectedData;

  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState<DeliveryValidationError_T[]>([]);
  const [isComplete, setIsComplete] = useState(false);

  const selectedGroups = [...fullySelectedGroups, ...partiallySelectedGroups];

  const targetDate =
    selectedGroups[0]?.shipments[0]?.target_fulfillment_date || "";

  // hard stops
  const invalidSelectionHardStop = hasInvalidAppleCustomerSelection(
    props.customerId,
    partiallySelectedGroups
  );
  const invalidStatusHardStop = !hasValidDeliveryStatusForGroup(selectedGroups);

  // soft stops
  const showExportDateWarning = hasExportDateOutsideShippingWeek(
    targetDate,
    selectedGroups
  );
  const missingDestinationUriItems = getMissingDestinations(selectedGroups);

  const { mutateAsync: postShipment, isPending } = useMutation({
    mutationFn: async () => {
      setProgress(0);
      setErrors([]);
      setIsComplete(false);

      for (let i = 0; i < selectedGroups.length; i++) {
        const group = selectedGroups[i];
        const fgsId = group.shipments[0].fgs_id;

        try {
          await postDataplorApi(
            `/v3/admin/fulfillment_group_shipments/${fgsId}/deliver`,
            {}
          );
        } catch (error) {
          setErrors((prev) => [
            ...prev,
            {
              fulfillment_group_id: group.fulfillmentGroupId,
              name: group.name,
              fulfillment_group_shipment_id: fgsId ?? null,
              error: error instanceof Error ? error.message : "Unknown error",
            },
          ]);
        }

        setProgress(((i + 1) / selectedGroups.length) * 100);
      }

      return true;
    },
    onSuccess: () => {
      setIsComplete(true);
    },
    onError: () => {
      setIsComplete(true);
    },
  });

  const handleSubmit = () => {
    postShipment();
  };

  const isSubmitDisabled =
    selectedGroups.length === 0 ||
    isPending ||
    isComplete ||
    invalidStatusHardStop ||
    invalidSelectionHardStop;

  return (
    <BaseShippingDialog
      {...props}
      title="Deliver Shipment"
      onSubmit={handleSubmit}
      submitText={isPending ? "Delivering..." : "Deliver"}
      submitDisabled={isSubmitDisabled}
    >
      <Box>
        {invalidStatusHardStop && (
          <Alert
            severity="error"
            sx={{ mb: 2, display: "flex", gap: 1 }}
            icon={false}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <RiErrorWarningLine
                size={20}
                style={{ color: theme.palette.error.main }}
              />
              <Typography variant="body2">
                Error: selections must have status "Waiting to ship" or "Failed
                at shipping".
              </Typography>
            </Box>
          </Alert>
        )}

        {!invalidStatusHardStop && invalidSelectionHardStop && (
          <Alert
            severity="error"
            sx={{ mb: 2, display: "flex", gap: 1 }}
            icon={false}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <RiErrorWarningLine
                size={20}
                style={{ color: theme.palette.error.main }}
              />
              <Typography variant="body2">
                Error: For this customer, fulfillment groups must be selected to
                proceed.
              </Typography>
            </Box>
          </Alert>
        )}

        {!invalidSelectionHardStop && selectedGroups.length === 0 && (
          <Alert
            severity="info"
            sx={{ mb: 2, display: "flex", gap: 1 }}
            icon={false}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <RiErrorWarningLine
                size={20}
                style={{ color: theme.palette.info.main }}
              />
              <Typography variant="body2">
                Please select at least one fulfillment group to proceed.
              </Typography>
            </Box>
          </Alert>
        )}

        {!invalidStatusHardStop && showExportDateWarning && (
          <Alert severity="warning" sx={{ mb: 2 }} icon={false}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <RiErrorWarningLine
                size={20}
                style={{ color: theme.palette.warning.main }}
              />
              <Typography variant="body2">
                Warning: Some fulfillment exports are greater than 7 days from
                current day. Do you want to proceed?"
              </Typography>
            </Box>
          </Alert>
        )}

        {!invalidStatusHardStop && missingDestinationUriItems.length > 0 && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }} icon={false}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <RiErrorWarningLine
                  size={20}
                  style={{ color: theme.palette.warning.main }}
                />
                <Typography variant="body2">
                  Warning: Some deliveries have missing destination URIs. Do you
                  want to proceed?
                </Typography>
              </Box>
            </Alert>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {missingDestinationUriItems.map((item) => (
                <Chip
                  key={`${item.fulfillmentGroupId}-${item.deliveryId}`}
                  label={`${item.groupName} - ${item.deliveryName}`}
                />
              ))}
            </Box>
          </>
        )}

        {!invalidStatusHardStop &&
          !invalidSelectionHardStop &&
          selectedGroups.length > 0 && (
            <Box>
              <Typography variant="body2">
                {`Are you sure you want to deliver ${totalSelectedShipments} selected ${pluralize(
                  "shipment",
                  totalSelectedShipments
                )}?`}
              </Typography>

              {(isPending || isComplete) && (
                <Box sx={{ width: "100%", mt: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Processed: {Math.round(progress)}% (
                    {Math.floor((progress / 100) * selectedGroups.length)} of{" "}
                    {selectedGroups.length})
                  </Typography>
                </Box>
              )}

              {isComplete && errors.length === 0 && (
                <Alert severity="success" sx={{ mt: 2 }} icon={false}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <RiCheckboxCircleLine size={20} color="success.main" />
                    <Typography variant="body2">
                      {` Successfully delivered ${
                        selectedGroups.length
                      } selected
                      fulfillment ${pluralize(
                        "group",
                        selectedGroups.length
                      )}.`}
                    </Typography>
                  </Box>
                </Alert>
              )}

              {isComplete && errors.length > 0 && (
                <>
                  <Alert severity="error" sx={{ mt: 2 }} icon={false}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <RiErrorWarningLine size={20} color="error.main" />
                      <Typography variant="body2">
                        {`Failed to deliver {errors.length} fulfillment ${pluralize(
                          "groups",
                          errors.length
                        )}:`}
                      </Typography>
                    </Box>
                  </Alert>
                  <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                    {errors.map((error) => (
                      <li key={error.fulfillment_group_id}>
                        {error.name} (FG ID: {error.fulfillment_group_id}) (FGS
                        ID: {error.fulfillment_group_shipment_id})
                        {error.error && `: ${error.error}`}
                      </li>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
      </Box>
    </BaseShippingDialog>
  );
};

export default DeliverShipmentDialog;
